import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  SetOccurrenceStatusInput, TaskOccurrenceAssignee, TaskStatus, TaskType,
} from '@pms/shared';
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from '@/components/ui/select';
import { tasksService } from '@/services/tasks.service';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { ALL_STATUSES, REVIEWABLE_TYPES, STATUS_META } from './taskConstants';
import { StatusRemarkModal } from './StatusRemarkModal';
import type { VoiceNoteDraft } from './VoiceNoteRecorder';

interface StatusDropdownProps {
  row: TaskOccurrenceAssignee;
  /**
   * The parent task's type. Passed explicitly rather than read off `row.task_type` —
   * that field is optional and the task-detail query does not populate it, so relying
   * on it would silently fall through to the wrong option list.
   */
  taskType: TaskType;
  onChanged?: () => void;
  disabled?: boolean;
}

function Dot({ hex }: { hex: string }) {
  return <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: hex }} />;
}

/** Statuses that cannot be set without a remark — these open the modal first. */
const NEEDS_REMARK: TaskStatus[] = ['discarded', 'needs_improvement'];

/**
 * The task creator's status control: every status, settable from any status.
 *
 * That breadth is deliberate — the creator owns the task and gets a manual override, and
 * the server agrees. Assignees get `AssigneeActionButton` instead, because they cannot
 * approve their own work or discard a task.
 */
export function StatusDropdown({ row, taskType, onChanged, disabled }: StatusDropdownProps) {
  const qc = useQueryClient();
  const status = row.status as TaskStatus;
  const meta = STATUS_META[status];

  // A daily task has no review step — its Complete resolves straight to Completed — so
  // "In Review" is a dead end there: nothing would move it on, and it would sit in the
  // reviewer's queue for a task that never gets reviewed. The current status is always
  // kept so an existing value still renders in the trigger.
  const options = REVIEWABLE_TYPES.includes(taskType)
    ? ALL_STATUSES
    : ALL_STATUSES.filter((s) => s !== 'for_review' || s === status);

  // Which remark-requiring status the modal is currently collecting for, or null.
  const [remarkFor, setRemarkFor] = useState<'discarded' | 'needs_improvement' | null>(null);

  const setStatus = useMutation({
    mutationFn: (v: { input: SetOccurrenceStatusInput; voice?: VoiceNoteDraft | null }) =>
      tasksService.setStatusWithVoice(row.id, v.input, v.voice ?? null),
    onSuccess: (result) => {
      // The status change stands even if only the recording failed.
      if (result.voiceError) toast.warning(`Status updated, but the voice note failed to upload. (${result.voiceError})`);
      else toast.success('Status updated');
      setRemarkFor(null);
      qc.invalidateQueries({ queryKey: ['tasks'] });
      onChanged?.();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Discarding and sending back both demand a remark, so they open the modal instead of
  // firing straight away. Nothing is mutated until the remark is given; cancelling needs
  // no revert because the trigger renders `row.status`, which has not changed.
  function handleSelect(next: string) {
    if (next === status) return;
    if (NEEDS_REMARK.includes(next as TaskStatus)) {
      setRemarkFor(next as 'discarded' | 'needs_improvement');
      return;
    }
    setStatus.mutate({ input: { status: next as SetOccurrenceStatusInput['status'] } });
  }

  return (
    <>
    <Select
      value={status}
      onValueChange={handleSelect}
      disabled={disabled || setStatus.isPending}
    >
      <SelectTrigger
        className={cn(
          'h-8 w-auto gap-2 rounded-full border-border/60 px-3 text-xs font-semibold',
          meta.className,
        )}
      >
        <span className="flex items-center gap-1.5">
          <Dot hex={meta.hex} />
          {meta.label}
        </span>
      </SelectTrigger>
      <SelectContent>
        {options.map((s) => (
          <SelectItem key={s} value={s}>
            <span className="flex items-center gap-2">
              <Dot hex={STATUS_META[s].hex} />
              {STATUS_META[s].label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>

    {remarkFor && (
      <StatusRemarkModal
        status={remarkFor}
        open
        onOpenChange={(o) => { if (!o) setRemarkFor(null); }}
        busy={setStatus.isPending}
        onConfirm={(note, voice) => setStatus.mutate({ input: { status: remarkFor, note }, voice })}
      />
    )}
    </>
  );
}
